<template>
    <div class="cf-root">
        <!-- List View -->
        <div v-if="currentEdit == null">
            <div class="cf-list-header">
                <b-input-group size="sm" class="cf-search">
                    <b-input-group-prepend is-text>
                        <b-icon icon="search" font-scale="0.85"></b-icon>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model="searchFilter"
                        :placeholder="$t('searchFunctions')"
                    ></b-form-input>
                    <b-input-group-append v-if="searchFilter">
                        <b-button variant="outline-secondary" @click="searchFilter = ''">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
                <b-button @click="create" variant="primary" size="sm" class="cf-create-btn">
                    <b-icon icon="plus" class="mr-1"></b-icon>{{ $t('create') }}
                </b-button>
            </div>

            <!-- Function Rows -->
            <div v-if="filteredContent.length > 0" class="cf-list">
                <div
                    v-for="(func, idx) in filteredContent"
                    :key="idx"
                    class="cf-row"
                    @click="edit(func)"
                >
                    <div class="cf-row-main">
                        <div class="cf-row-name">
                            <b-icon icon="braces" class="cf-row-icon"></b-icon>
                            <span class="cf-fname">{{ func.name }}</span>
                        </div>
                        <span v-if="func.isExtension" class="cf-ext-chip">
                            <b-icon icon="puzzle" font-scale="0.75" class="mr-1"></b-icon>{{ func.extensionName }}
                        </span>
                        <span v-else class="cf-src-chip">component</span>
                    </div>
                    <div class="cf-row-preview" v-if="func.expr">
                        <code>{{ truncateExpr(func.expr) }}</code>
                    </div>
                </div>
            </div>

            <!-- Empty / No Results -->
            <div v-else-if="searchFilter" class="cf-empty">
                <b-icon icon="search" font-scale="2" class="mb-3"></b-icon>
                <p>No functions matching "{{ searchFilter }}"</p>
                <b-button variant="outline-primary" size="sm" @click="searchFilter = ''">Clear search</b-button>
            </div>
            <div v-else class="cf-empty">
                <b-icon icon="braces" font-scale="2.5" class="mb-3"></b-icon>
                <p>{{ $t('noFunctionsDefined') }}</p>
                <b-button variant="primary" size="sm" @click="create">
                    <b-icon icon="plus" class="mr-1"></b-icon>{{ $t('create') }}
                </b-button>
            </div>
        </div>

        <!-- Editor View -->
        <div v-else class="cf-editor">
            <!-- Editor Header -->
            <div class="cf-editor-header">
                <div class="cf-editor-left">
                    <button class="cf-back" @click="currentEdit = null">
                        <b-icon icon="arrow-left" font-scale="1"></b-icon>
                    </button>
                    <div class="cf-editor-identity">
                        <div class="cf-editor-title-row">
                            <b-icon icon="braces" class="cf-editor-icon"></b-icon>
                            <span v-if="currentEdit.mode === 'edit'" class="cf-editor-name">{{ currentEdit.name }}</span>
                            <span v-else class="cf-editor-name cf-editor-name--new">New Function</span>
                            <span v-if="currentEdit.readonly" class="cf-readonly-chip">
                                <b-icon icon="lock" font-scale="0.75" class="mr-1"></b-icon>read-only
                            </span>
                        </div>
                        <span v-if="currentEdit.extensionName" class="cf-ext-chip">
                            <b-icon icon="puzzle" font-scale="0.75" class="mr-1"></b-icon>{{ currentEdit.extensionName }}
                        </span>
                    </div>
                </div>
                <div class="cf-editor-actions">
                    <b-button v-if="currentEdit.mode === 'edit'" size="sm" variant="outline-warning" @click="showTestPanel = !showTestPanel">
                        <b-icon :icon="showTestPanel ? 'chevron-up' : 'play-circle'" class="mr-1"></b-icon>
                        {{ showTestPanel ? $t('hideTest') : $t('test') }}
                    </b-button>
                    <b-button v-if="currentEdit.mode === 'edit' && !currentEdit.readonly" size="sm" variant="outline-danger" @click="remove">
                        <b-icon icon="trash" class="mr-1"></b-icon>{{ $t('remove') }}
                    </b-button>
                    <b-button v-if="!currentEdit.readonly" size="sm" variant="success" @click="save">
                        <b-icon icon="check2" class="mr-1"></b-icon>{{ $t('save') }}
                    </b-button>
                </div>
            </div>

            <!-- Name Input (create mode) -->
            <div v-if="currentEdit.mode === 'create'" class="cf-name-input">
                <label>{{ $t('name') }}</label>
                <protokol-invalid-feedback :errors="validator.fieldErrors('name')">
                    <b-form-input v-model="currentEdit.name" :placeholder="$t('name')" size="sm"></b-form-input>
                </protokol-invalid-feedback>
            </div>

            <!-- Code Editor -->
            <div class="cf-code">
                <CodeEditor v-model="currentEdit.expr"
                    :options="{
                        mode: 'javascript',
                        title: currentEdit.name,
                    }"
                    :withButton="false"
                    :enableProtokolTypes="true"
                    :height="showTestPanel ? 400 : 700"
                    :readOnly="currentEdit.readonly"
                    :scopeDeclarations="componentScopeDts"
                ></CodeEditor>
            </div>

            <!-- Test Panel -->
            <transition name="cf-slide">
                <div v-if="showTestPanel" class="cf-test">
                    <div class="cf-test-header">
                        <div class="cf-test-title">
                            <b-icon icon="terminal" class="mr-2"></b-icon>
                            <span>{{ $t('testFunction') }}</span>
                            <span v-if="testStatus" class="cf-test-status" :class="'cf-test-status--' + testStatus">
                                {{ testStatus }}
                            </span>
                            <small v-if="testExecutionTime" class="text-muted ml-2">{{ testExecutionTime }}ms</small>
                        </div>
                        <div class="cf-test-actions">
                            <b-button size="sm" variant="outline-secondary" @click="clearTest" class="mr-2">
                                {{ $t('clear') }}
                            </b-button>
                            <b-button size="sm" variant="success" @click="runTest" :disabled="testLoading">
                                <b-spinner small v-if="testLoading" class="mr-1"></b-spinner>
                                <b-icon v-else icon="play-fill" class="mr-1"></b-icon>
                                {{ testLoading ? $t('testing') : $t('runTest') }}
                            </b-button>
                        </div>
                    </div>

                    <!-- Signature Hints -->
                    <div v-if="functionSignatureParams" class="cf-signature">
                        <span class="cf-sig-label">
                            <b-icon icon="list-check" font-scale="0.85" class="mr-1"></b-icon>Signature:
                        </span>
                        <span v-for="(typeNode, paramName) in functionSignatureParams" :key="paramName" class="cf-sig-param">
                            <code>{{ paramName }}</code>
                            <span class="cf-sig-type">{{ typeNode.type || typeNode }}</span>
                            <span v-if="typeNode.required !== false" class="cf-sig-req">*</span>
                        </span>
                    </div>

                    <div class="cf-test-body">
                        <!-- Input -->
                        <div class="cf-test-col">
                            <div class="cf-test-col-header">
                                <span>{{ $t('inputData') }}</span>
                                <b-button-group size="sm">
                                    <b-button
                                        :variant="inputMode === 'simple' ? 'primary' : 'outline-secondary'"
                                        @click="inputMode = 'simple'"
                                        class="cf-mode-btn"
                                    >Simple</b-button>
                                    <b-button
                                        :variant="inputMode === 'json' ? 'primary' : 'outline-secondary'"
                                        @click="inputMode = 'json'"
                                        class="cf-mode-btn"
                                    >JSON</b-button>
                                </b-button-group>
                            </div>
                            <div class="cf-test-input">
                                <template v-if="inputMode === 'simple'">
                                    <b-form-textarea
                                        v-model="simpleInput"
                                        rows="6"
                                        :placeholder="$t('enterTestData')"
                                        :state="simpleInputValid"
                                        class="cf-textarea"
                                    ></b-form-textarea>
                                    <b-form-invalid-feedback v-if="simpleInputValid === false">
                                        {{ $t('invalidJsonFormat') }}
                                    </b-form-invalid-feedback>
                                </template>
                                <template v-else>
                                    <CodeEditor
                                        v-model="testInput"
                                        :options="{ mode: 'json', showFullscreenButton: false, title: $t('testInput') }"
                                        :withButton="false"
                                        :height="180"
                                    ></CodeEditor>
                                </template>
                            </div>
                        </div>

                        <!-- Output -->
                        <div class="cf-test-col">
                            <div class="cf-test-col-header">
                                <span>{{ $t('output') }}</span>
                                <b-button v-if="testOutput" size="sm" variant="outline-secondary" @click="copyOutput" class="cf-mode-btn">
                                    <b-icon icon="clipboard" font-scale="0.85"></b-icon>
                                </b-button>
                            </div>
                            <div class="cf-test-output">
                                <div v-if="testLoading" class="cf-test-placeholder">
                                    <b-spinner small></b-spinner>
                                    <span>{{ $t('runningTest') }}...</span>
                                </div>
                                <div v-else-if="testOutput">
                                    <CodeEditor
                                        :value="testOutput"
                                        :options="{mode: 'json', readOnly: true}"
                                        :withButton="false"
                                        :height="180"
                                    ></CodeEditor>
                                </div>
                                <div v-else class="cf-test-placeholder">
                                    <b-icon icon="play-circle" font-scale="1.5"></b-icon>
                                    <span>{{ $t('runTestToSeeOutput') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import { componentFunctionScopeToDts } from '@/utils/idlToDts'

export default {
    components: {
        CodeEditor
    },
    props: {
        id: {
            type: String
        },

        value: {
            type: Array,
            default: () => []
        },

        config: {
            type: Object,
            default: () => {}
        },

        extensions: {
            type: Array,
            default: () => []
        },

        idl: {
            type: Object,
            default: null
        }
    },

    data: function() {
        return {
            currentEdit: null,
            createSidebar: false,
            testOutput: null,
            testInput: null,
            showTestPanel: false,
            testLoading: false,
            testStatus: null,
            testExecutionTime: null,
            inputMode: 'simple',
            simpleInput: '',
            searchFilter: '',
            testTemplates: [
                { name: 'Empty Object', value: '{}' },
                { name: 'Sample Data', value: '{"key": "value", "number": 123}' },
                { name: 'Array', value: '[1, 2, 3]' },
                { name: 'User Object', value: '{"id": 1, "name": "John", "email": "john@example.com"}' }
            ],
            validator: this.$validator()
        }
    },

    computed: {
        functionSignatureParams() {
            if (!this.idl || !this.currentEdit || !this.currentEdit.name) return null
            const fns = this.idl.functions
            if (!Array.isArray(fns)) return null
            const fn = fns.find(f => f.name === this.currentEdit.name)
            return fn && fn.input && Object.keys(fn.input).length ? fn.input : null
        },

        componentScopeDts() {
            return componentFunctionScopeToDts(this.functionSignatureParams)
        },

        content: {
            get() {
                return this.value || []
            },

            set(val) {
                this.$emit('input', val)
            }
        },

        allFunctions() {
            const componentFunctions = this.content.map(func => ({
                ...func,
                isExtension: false,
                extensionName: null
            }))

            const extensionFunctions = []
            if (this.extensions && Array.isArray(this.extensions)) {
                this.extensions.forEach(extension => {
                    if (extension.is_active && extension.functions && Array.isArray(extension.functions)) {
                        extension.functions.forEach(func => {
                            extensionFunctions.push({
                                ...func,
                                isExtension: true,
                                extensionName: extension.name,
                                readonly: true
                            })
                        })
                    }
                })
            }

            return [...componentFunctions, ...extensionFunctions]
        },

        filteredContent() {
            if (!this.searchFilter) {
                return this.allFunctions
            }

            const filter = this.searchFilter.toLowerCase()
            return this.allFunctions.filter(func => {
                return func.name.toLowerCase().includes(filter) ||
                       (func.expr && func.expr.toLowerCase().includes(filter)) ||
                       (func.extensionName && func.extensionName.toLowerCase().includes(filter))
            })
        },

        simpleInputValid() {
            if (!this.simpleInput) return null
            try {
                JSON.parse(this.simpleInput)
                return true
            } catch {
                return false
            }
        }
    },

    methods: {
        truncateExpr(expr) {
            if (!expr) return ''
            const firstLine = expr.split('\n').find(l => l.trim()) || ''
            const trimmed = firstLine.trim()
            return trimmed.length > 80 ? trimmed.substring(0, 80) + '...' : trimmed
        },

        create() {
            this.currentEdit = {
                mode: 'create',
                name: '',
                expr: ''
            }

            this.validate()
        },

        save() {
            this.validator.revalidate()

            if (!this.validator.pass()) {
                return
            }

            if (this.currentEdit.mode == 'create') {
                const exist = this.content.find(f => f.name == this.currentEdit.name)
                if (exist) {
                    this.$bvToast.toast(this.$t('functionExists'), {
                        title: 'Error',
                        variant: 'danger',
                        autoHideDelay: 5000
                    })

                    return
                }

                this.content.push(this.currentEdit)
                this.currentEdit = null

            } else if (this.currentEdit.mode == 'edit') {
                const idx = this.content.findIndex(f => f.name == this.currentEdit.name)

                this.$set(this.content, idx, {
                    name: this.currentEdit.name,
                    expr: this.currentEdit.expr
                })

                this.currentEdit = null
            }

            this.$emit('input', this.content)
        },

        async test() {
            await this.runTest()
        },

        async runTest() {
            let inputData = this.testInput
            if (this.inputMode === 'simple' && this.simpleInput) {
                try {
                    inputData = this.simpleInput
                    this.testInput = inputData
                } catch (e) {
                    this.$bvToast.toast(this.$t('invalidJsonInput'), {
                        title: 'Error',
                        variant: 'danger',
                        autoHideDelay: 3000
                    })
                    return
                }
            }

            if (!inputData || inputData.trim() === "") {
                inputData = null
            }

            this.testLoading = true
            this.testStatus = 'running'
            const startTime = performance.now()

            const functionSignature = this.currentEdit.extensionName
                ? `${this.currentEdit.extensionName}.${this.currentEdit.name}`
                : this.currentEdit.name

            try {
                const resp = await this.$http.post(`/v3/system/component/${this.id}/function/${functionSignature}`, {
                    data: inputData ? JSON.parse(inputData) : null,
                    options: {
                        expression: this.currentEdit.expr,
                        config: this.config,
                        include_logs: true
                    }
                })

                this.testOutput = JSON.stringify(resp.data, null, 4)
                this.testStatus = 'success'
                this.testExecutionTime = Math.round(performance.now() - startTime)

            } catch (e) {
                console.log("error", e)
                this.testOutput = JSON.stringify((e.response && e.response.data) || { error: e.message }, null, 4)
                this.testStatus = 'error'
                this.testExecutionTime = Math.round(performance.now() - startTime)
            } finally {
                this.testLoading = false
            }
        },

        loadTemplate(template) {
            if (this.inputMode === 'simple') {
                this.simpleInput = template.value
            } else {
                this.testInput = template.value
            }
        },

        clearTest() {
            this.testInput = null
            this.simpleInput = ''
            this.testOutput = null
            this.testStatus = null
            this.testExecutionTime = null
        },

        copyOutput() {
            if (this.testOutput) {
                navigator.clipboard.writeText(this.testOutput).then(() => {
                    this.$bvToast.toast(this.$t('copiedToClipboard'), {
                        title: this.$t('success'),
                        variant: 'success',
                        autoHideDelay: 2000
                    })
                })
            }
        },

        edit(f) {
            this.currentEdit = {
                mode: 'edit',
                name: f.name,
                expr: f.expr,
                readonly: !!f.readonly,
                extensionName: f.extensionName || null
            }

            this.showTestPanel = false
            this.clearTest()
            this.validate()
        },

        async remove() {
            const confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure') + "?", {
                title: this.$t('confirm'),
                size: 'sm',
                okVariant: 'danger',
                okTitle: this.$t('yes'),
                cancelTitle: this.$t('no'),
                footerClass: 'p-2'
            })

            if (!confirm) return

            const idx = this.content.findIndex(f => f.name == this.currentEdit.name)
            this.content.splice(idx, 1)
            this.currentEdit = null
        },

        validate() {
            const jsReservedKeywords = [
                'break','case','catch','class','const','continue','debugger','default',
                'delete','do','else','export','extends','false','finally','for',
                'function','if','import','in','instanceof','let','new','null',
                'return','static','super','switch','this','throw','true','try',
                'typeof','undefined','var','void','while','with','yield','enum','await'
            ]
            this.validator.validate(this.currentEdit, {
                default: (model, errors) => {
                    if (!model.name) errors.add('name', [this.$t('fieldRequired')])
                    else if (jsReservedKeywords.includes(model.name.toLowerCase())) {
                        errors.add('name', [this.$t('functionNameReservedKeyword')])
                    }
                }
            })
        }
    },

    watch: {
        inputMode(newMode) {
            if (newMode === 'json' && this.simpleInput && this.simpleInputValid) {
                this.testInput = this.simpleInput
            } else if (newMode === 'simple' && this.testInput) {
                this.simpleInput = this.testInput
            }
        },

        simpleInput(newVal) {
            if (this.inputMode === 'simple' && this.simpleInputValid) {
                this.testInput = newVal
            }
        }
    }
}
</script>

<style scoped>
/* ── List Header ───────────────────────────────── */
.cf-list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.cf-search {
    flex: 1;
    max-width: 360px;
}

.cf-search .input-group-text {
    background: transparent;
    border-right: 0;
    color: var(--text-muted);
}

.cf-search .form-control {
    border-left: 0;
}

.cf-create-btn {
    white-space: nowrap;
    font-weight: 500;
}

/* ── Function List ─────────────────────────────── */
.cf-list {
    border: 1px solid var(--border-default);
    border-radius: 8px;
    overflow: hidden;
}

.cf-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-default);
    cursor: pointer;
    transition: background 0.12s;
}

.cf-row:last-child {
    border-bottom: none;
}

.cf-row:hover {
    background: var(--bg-card-secondary);
}

.cf-row-main {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cf-row-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.cf-row-icon {
    color: var(--text-muted);
    flex-shrink: 0;
    font-size: 14px;
}

.cf-fname {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.cf-row-preview {
    padding-left: 22px;
}

.cf-row-preview code {
    font-size: 11px;
    color: var(--text-muted);
    background: none;
    padding: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    max-width: 500px;
}

/* ── Chips ─────────────────────────────────────── */
.cf-ext-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(66, 153, 225, 0.10);
    color: var(--color-info, #4299e1);
    border: 1px solid rgba(66, 153, 225, 0.2);
    white-space: nowrap;
}

.cf-src-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted);
    border: 1px solid var(--border-default);
    white-space: nowrap;
}

.cf-readonly-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    background: rgba(237, 137, 54, 0.10);
    color: var(--warning-start, #ed8936);
    border: 1px solid rgba(237, 137, 54, 0.2);
    white-space: nowrap;
}

/* ── Empty State ───────────────────────────────── */
.cf-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    color: var(--text-muted);
    font-size: 14px;
    border: 1px dashed var(--border-default);
    border-radius: 8px;
}

/* ── Editor ────────────────────────────────────── */
.cf-editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 14px;
    border-bottom: 1px solid var(--border-default);
    margin-bottom: 14px;
    gap: 12px;
    flex-wrap: wrap;
}

.cf-editor-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.cf-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid var(--border-default);
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.12s;
    flex-shrink: 0;
}

.cf-back:hover {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    border-color: var(--border-hover);
}

.cf-editor-identity {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.cf-editor-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cf-editor-icon {
    color: var(--text-muted);
    font-size: 15px;
    flex-shrink: 0;
}

.cf-editor-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.cf-editor-name--new {
    font-family: inherit;
    color: var(--text-muted);
    font-style: italic;
}

.cf-editor-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

/* ── Name Input ────────────────────────────────── */
.cf-name-input {
    margin-bottom: 14px;
}

.cf-name-input label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin-bottom: 4px;
    display: block;
}

/* ── Code Area ─────────────────────────────────── */
.cf-code {
    border-radius: 8px;
    overflow: hidden;
}

/* ── Test Panel ────────────────────────────────── */
.cf-test {
    margin-top: 14px;
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-card, #313338);
    overflow: hidden;
}

.cf-test-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-default);
    gap: 12px;
    flex-wrap: wrap;
}

.cf-test-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
}

.cf-test-actions {
    display: flex;
    align-items: center;
}

.cf-test-status {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    margin-left: 8px;
}

.cf-test-status--success {
    background: rgba(72, 187, 120, 0.10);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.2);
}

.cf-test-status--error {
    background: rgba(245, 101, 101, 0.10);
    color: var(--danger-start, #f56565);
    border: 1px solid rgba(245, 101, 101, 0.2);
}

.cf-test-status--running {
    background: rgba(237, 137, 54, 0.10);
    color: var(--warning-start, #ed8936);
    border: 1px solid rgba(237, 137, 54, 0.2);
}

/* ── Signature ─────────────────────────────────── */
.cf-signature {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--border-default);
    font-size: 12px;
    flex-wrap: wrap;
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.02));
}

.cf-sig-label {
    color: var(--text-muted);
    font-weight: 600;
    white-space: nowrap;
}

.cf-sig-param {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 4px;
    background: var(--bg-card, #313338);
    border: 1px solid var(--border-default);
}

.cf-sig-param code {
    color: var(--text-primary);
    background: none;
    padding: 0;
    font-size: 12px;
}

.cf-sig-type {
    color: var(--primary-start, #6c80ff);
    font-size: 11px;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.cf-sig-req {
    color: var(--danger-start, #f56565);
    font-weight: 700;
}

/* ── Test Body ─────────────────────────────────── */
.cf-test-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    min-height: 0;
}

.cf-test-col {
    display: flex;
    flex-direction: column;
}

.cf-test-col + .cf-test-col {
    border-left: 1px solid var(--border-default);
}

.cf-test-col-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-default);
}

.cf-mode-btn {
    font-size: 11px !important;
    padding: 1px 8px !important;
    line-height: 1.5;
}

.cf-test-input,
.cf-test-output {
    flex: 1;
    min-height: 0;
}

.cf-textarea {
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    border: none;
    border-radius: 0;
    resize: none;
    height: 100%;
}

.cf-test-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px;
    color: var(--text-muted);
    font-size: 13px;
}

/* ── Transition ────────────────────────────────── */
.cf-slide-enter-active,
.cf-slide-leave-active {
    transition: all 0.2s ease;
}

.cf-slide-enter,
.cf-slide-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

/* ── Responsive ────────────────────────────────── */
@media (max-width: 768px) {
    .cf-test-body {
        grid-template-columns: 1fr;
    }

    .cf-test-col + .cf-test-col {
        border-left: none;
        border-top: 1px solid var(--border-default);
    }

    .cf-editor-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .cf-editor-actions {
        width: 100%;
        justify-content: flex-end;
    }

    .cf-list-header {
        flex-direction: column;
        align-items: stretch;
    }

    .cf-search {
        max-width: none;
    }
}
</style>
